import { AttemptStatus } from '__generated__/graphql-types';
import type {
  AttemptSummary,
  RateLimitSummary,
  SubmissionLimitSummary,
  TimeAndSubmissionLimitSummary,
  TimeLimitSummary,
} from '__generated__/graphql-types-do-not-use';

export type AttemptSummaryWithTimeLimit = TimeLimitSummary | TimeAndSubmissionLimitSummary;

export type AttemptSummaryWithSubmissionLimit = SubmissionLimitSummary | TimeAndSubmissionLimitSummary;

export type AttemptSummaryWithRateLimit = RateLimitSummary;

export type AttemptSummaryWithAttemptStatus = TimeLimitSummary | SubmissionLimitSummary | TimeAndSubmissionLimitSummary;

export const doesAttemptSummaryHaveTimeLimit = (
  attemptSummary: AttemptSummary
): attemptSummary is AttemptSummaryWithTimeLimit => {
  const types: Array<AttemptSummary['__typename']> = ['TimeLimitSummary', 'TimeAndSubmissionLimitSummary'];
  return types.includes(attemptSummary.__typename);
};

export const doesAttemptSummaryHaveSubmissionLimit = (
  attemptSummary: AttemptSummary
): attemptSummary is AttemptSummaryWithSubmissionLimit => {
  const types: Array<AttemptSummary['__typename']> = ['SubmissionLimitSummary', 'TimeAndSubmissionLimitSummary'];
  return types.includes(attemptSummary.__typename);
};

export const doesAttemptSummaryHaveAttemptRateLimit = (
  attemptSummary: AttemptSummary
): attemptSummary is AttemptSummaryWithRateLimit => {
  const types: Array<AttemptSummary['__typename']> = ['RateLimitSummary'];
  return types.includes(attemptSummary.__typename);
};

export const doesAttemptSummaryHaveAttemptStatus = (
  attemptSummary: AttemptSummary
): attemptSummary is AttemptSummaryWithAttemptStatus => {
  return 'attemptStatus' in attemptSummary;
};

export const doesLaunchingLabConsumeAnAttempt = (
  attemptSummary: AttemptSummary
): attemptSummary is AttemptSummaryWithTimeLimit => {
  return doesAttemptSummaryHaveTimeLimit(attemptSummary) && attemptSummary.attemptStatus === 'NOT_STARTED';
};

export const extractAttemptSummary = (attemptSummary: AttemptSummary | null) => {
  let remainingSubmissions: number | undefined;
  let remainingSubmissionsNow: number | undefined;
  let remainingDuration: number | undefined;

  let allowedSubmissions: number | undefined;
  let isMaxAttemptsReached = false;
  let shouldShowTimerBanner = false;
  let shouldShowAttemptModal = false;

  if (attemptSummary) {
    if (doesAttemptSummaryHaveSubmissionLimit(attemptSummary)) {
      allowedSubmissions = attemptSummary.allowedSubmissions;
      remainingSubmissions = attemptSummary.remainingSubmissions;
      isMaxAttemptsReached = remainingSubmissions === 0;
    }

    if (doesAttemptSummaryHaveTimeLimit(attemptSummary)) {
      remainingDuration = attemptSummary.remainingDuration;
      if (attemptSummary.attemptStatus === AttemptStatus.NotStarted) {
        // If we are showing the start-attempt modal, we want to make sure that the timer banner
        // is not showing, because it is strange to see an idle timer banner in the background
        // of the start-attempt modal. We are assuming at this point that `shouldShowTimerBanner`
        // is still `false` as it was initialized with.
        shouldShowAttemptModal = true;
      } else {
        shouldShowTimerBanner = true;
      }
    }

    if (doesAttemptSummaryHaveAttemptRateLimit(attemptSummary)) {
      remainingSubmissionsNow = attemptSummary.remainingSubmissionsNow;
      isMaxAttemptsReached = remainingSubmissionsNow === 0;
    }
  }

  const isSubmissionEnabled =
    (typeof remainingSubmissions !== 'undefined' ? remainingSubmissions > 0 : true) &&
    (typeof remainingDuration !== 'undefined' ? remainingDuration > 0 : true) &&
    (typeof remainingSubmissionsNow !== 'undefined' ? remainingSubmissionsNow > 0 : true);

  return {
    allowedSubmissions,
    isMaxAttemptsReached,
    isSubmissionEnabled,
    remainingDuration,
    remainingSubmissions,
    remainingSubmissionsNow,
    shouldShowTimerBanner,
    shouldShowAttemptModal,
  };
};

export const isSubmissionEnabled = (attemptSummary: AttemptSummary | null): boolean => {
  return extractAttemptSummary(attemptSummary).isSubmissionEnabled;
};
